import SelectedFileItem from '@/components/MantineFileManager/btn/SelectedFileItem';
import {
  useDeleteFilesMutation,
  useUpdateFileMutation,
  useUploadFileMutation,
} from '@/features/api/endpoints/fileEndpoints';
import {
  useCreateFileSubmissionMutation,
  type FileSubmissionCreate,
} from '@/features/api/endpoints/fileSubmissionEndpoints';
import type { Role } from '@/models/types';
import { getUploadErrorKey } from '@/shared/utils/upload-utils';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button, List, Select, Stack, Text, Textarea } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconUpload } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const MAX_FILE_SIZE_LABEL = '50 Mo';
const ACCEPTED_FILE_TYPES: Record<string, string[]> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv'],
  'application/zip': ['.zip'],
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/webp': ['.webp'],
  'image/gif': ['.gif'],
  'video/mp4': ['.mp4'],
  'text/plain': ['.txt'],
};

type Props = {
  recipientGroups: Role[];
};

const FileSubmissionForm = ({ recipientGroups }: Props) => {
  const { t } = useTranslate();
  const [uploadFile] = useUploadFileMutation();
  const [updateFile] = useUpdateFileMutation();
  const [deleteFiles] = useDeleteFilesMutation();
  const [createFileSubmission] = useCreateFileSubmissionMutation();

  const formSchema = z.object({
    recipientgroup: z.number().min(1, { message: t('file.submission.validation.required') }),
    files: z.array(z.instanceof(File)).min(1),
    comment: z.string().optional(),
  });

  type FormValues = z.infer<typeof formSchema>;

  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      recipientgroup: undefined,
      files: [],
      comment: '',
    },
  });

  const onSubmit = async (values: FormValues) => {
    const uploadedFileIds: string[] = [];
    const recipientFolder = recipientGroups.find(role => role.id === values.recipientgroup);

    if (!recipientFolder) {
      return;
    }

    try {
      for (const file of values.files) {
        const formData = new FormData();
        formData.append('file', file);
        const uploadedFile = await uploadFile(formData).unwrap();
        uploadedFileIds.push(uploadedFile.id);

        await updateFile({
          id: uploadedFile.id,
          body: {
            folder: recipientFolder.uploadFolder,
          },
        }).unwrap();
      }

      const files: FileSubmissionCreate['files'] = uploadedFileIds.map(id => ({
        directus_files_id: id,
      }));

      await createFileSubmission({
        role: values.recipientgroup,
        comment: values.comment?.trim() || null,
        files,
      }).unwrap();

      notifications.show({
        color: 'green',
        message: t('file.submission.success'),
      });
      modals.closeAll();
    } catch (error: unknown) {
      if (uploadedFileIds.length > 0) {
        await deleteFiles(uploadedFileIds)
          .unwrap()
          .catch(() => undefined);
      }

      notifications.show({
        color: 'red',
        message: t(getUploadErrorKey(error, 'file.submission.error')),
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack gap="md">
        <Controller
          control={control}
          name="recipientgroup"
          render={({ field, fieldState }) => (
            <Select
              {...field}
              value={field.value ?? null}
              label={t('file.submission.recipientgroup.label')}
              placeholder={t('file.submission.recipientgroup.placeholder')}
              data={recipientGroups.map(group => ({
                value: group.id,
                label: group.name as string,
              }))}
              error={fieldState.error ? t('file.submission.validation.required') : undefined}
              withAsterisk
            />
          )}
        />
        <Controller
          control={control}
          name="files"
          render={({ field, fieldState }) => (
            <Stack gap="sm">
              <Dropzone
                onDrop={field.onChange}
                onReject={() => setError('files', { type: 'validate' })}
                multiple
                accept={ACCEPTED_FILE_TYPES}
                maxSize={MAX_FILE_SIZE}
                aria-invalid={Boolean(fieldState.error)}
                styles={{
                  root: {
                    borderColor: fieldState.error ? 'var(--mantine-color-red-6)' : undefined,
                  },
                }}
              >
                <Stack gap="xs" align="center" py="md" c={fieldState.error ? 'red.6' : undefined}>
                  <IconUpload size={28} />
                  <span>{t('file.submission.files.help')}</span>
                  <Text size="xs" ta="center">
                    {t('file.submission.files.constraints', { maxSize: MAX_FILE_SIZE_LABEL })}
                  </Text>
                  {fieldState.error && <span>{t('file.submission.validation.files')}</span>}
                </Stack>
              </Dropzone>
              {field.value.length > 0 && (
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    {t('file.submission.files.selected')}
                  </Text>
                  <List spacing="xs" size="sm" listStyleType="none" withPadding={false}>
                    {field.value.map(file => (
                      <SelectedFileItem
                        key={`${file.name}-${file.lastModified}`}
                        file={file}
                        onRemove={() =>
                          field.onChange(field.value.filter(selectedFile => selectedFile !== file))
                        }
                      />
                    ))}
                  </List>
                </Stack>
              )}
            </Stack>
          )}
        />
        <Controller
          control={control}
          name="comment"
          render={({ field }) => (
            <Textarea
              {...field}
              label={t('file.submission.comment.label')}
              placeholder={t('file.submission.comment.placeholder')}
              autosize
              minRows={6}
            />
          )}
        />
        <Button type="submit" loading={isSubmitting} leftSection={<IconUpload size={18} />}>
          {t('file.submission.submit')}
        </Button>
      </Stack>
    </form>
  );
};

export default FileSubmissionForm;
